import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronLeft, Loader2, Plus, Trash2, Pencil, Save, X, GripVertical,
  ExternalLink, Image as ImageIcon, Upload as UploadIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api, getAdminToken } from "../../lib/api";
import { uploadImage } from "../../lib/uploadImage";

/**
 * Admin · "The Latest" manager.
 *
 * Simple CRUD over the /api/admin/latest endpoints. Adding a new tile only
 * requires the brand name and an image — everything else (subtitle, caption,
 * external link, order) is optional and can be filled in later once the
 * production credits arrive.
 */
const EMPTY = {
  brand: "",
  subtitle: "",
  caption: "",
  image_url: "",
  external_link: "",
  order: 0,
  type: "campaign",
  model_id: "",
  model_slug: "",
  model_name: "",
  photographer: "",
  stylist: "",
  hair: "",
  makeup: "",
};

const TYPE_OPTIONS = [
  { value: "campaign", label: "Campaign" },
  { value: "editorial", label: "Editorial" },
  { value: "cover", label: "Cover" },
];

export default function LatestManager() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | {...doc}

  const load = () => {
    setLoading(true);
    api.get("/admin/latest")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!getAdminToken()) { navigate("/admin/login"); return; }
    load();
  }, [navigate]);

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.brand}"?`)) return;
    try {
      await api.delete(`/admin/latest/${it.id}`);
      toast.success("Removed from The Latest");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-[#0A0A0A] pt-32 flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div data-testid="latest-manager-page" className="min-h-screen bg-white text-[#0A0A0A] pt-24">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 pb-24">
        <Link
          to="/admin"
          data-testid="latest-manager-back"
          className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-black"
        >
          <ChevronLeft size={14} /> Back to dashboard
        </Link>

        <div className="mt-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="label-overline text-zinc-500">Site content · 03</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tighter font-light mt-3 leading-none">
              The Latest.
            </h1>
            <p className="text-zinc-600 mt-3 text-sm max-w-2xl leading-relaxed">
              The horizontal strip on the homepage below the hero. Add a brand
              + an image to publish. Fill in production credits later if you
              don&#39;t have them yet.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEditing({ ...EMPTY })}
            data-testid="latest-add"
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus size={14}/> Add work
          </button>
        </div>

        {/* Editor sheet — opens in-page (no modal, less friction) */}
        {editing && (
          <EditorSheet
            initial={editing}
            onCancel={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}

        {/* Existing list */}
        <div className="mt-12">
          {items.length === 0 && !editing && (
            <div className="border border-dashed border-black/15 bg-[#FAFAF9] p-12 text-center">
              <p className="label-overline text-zinc-400">Nothing published yet</p>
              <p className="text-sm text-zinc-500 mt-2">
                Click <span className="font-medium text-zinc-700">Add work</span> to publish your first tile.
              </p>
            </div>
          )}

          <div className="space-y-3">
            {items.map((it) => (
              <div
                key={it.id}
                data-testid={`latest-row-${it.id}`}
                className="flex items-stretch gap-4 border border-black/10 bg-white p-3"
              >
                <GripVertical size={16} className="text-zinc-300 self-center shrink-0" />
                <div className="w-16 h-20 bg-[#F5F5F5] shrink-0 overflow-hidden">
                  {it.image_url ? (
                    <img src={it.image_url} alt="" className="w-full h-full object-cover"/>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-400">
                      <ImageIcon size={16}/>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0 py-1">
                  <p className="font-serif text-lg leading-tight truncate">
                    {it.brand}
                    {it.subtitle && <span className="italic text-zinc-500"> — {it.subtitle}</span>}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1">
                    {it.type || "campaign"}{it.model_name ? ` · ${it.model_name}` : ""}
                  </p>
                  {it.caption && (
                    <p className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1 line-clamp-1">
                      {it.caption}
                    </p>
                  )}
                  {it.external_link && (
                    <a
                      href={it.external_link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[11px] text-zinc-500 hover:text-black mt-1 inline-flex items-center gap-1"
                    >
                      <ExternalLink size={10}/> {it.external_link.replace(/^https?:\/\//, "").slice(0, 40)}
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-1 self-center">
                  <button
                    type="button"
                    onClick={() => setEditing(it)}
                    data-testid={`latest-edit-${it.id}`}
                    className="p-2 text-zinc-600 hover:text-black hover:bg-black/5 transition-colors"
                    aria-label={`Edit ${it.brand}`}
                  >
                    <Pencil size={14}/>
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(it)}
                    data-testid={`latest-delete-${it.id}`}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 transition-colors"
                    aria-label={`Delete ${it.brand}`}
                  >
                    <Trash2 size={14}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-xs text-zinc-500 mt-10 leading-relaxed">
          Tiles appear on the homepage in ascending <span className="font-mono">order</span> (0
          first). Leave <span className="font-mono">order</span> alone for chronological display.
        </p>
      </div>
    </div>
  );
}

function EditorSheet({ initial, onCancel, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({
    brand: initial.brand || "",
    subtitle: initial.subtitle || "",
    caption: initial.caption || "",
    image_url: initial.image_url || "",
    external_link: initial.external_link || "",
    order: initial.order ?? 0,
    type: initial.type || "campaign",
    model_id: initial.model_id || "",
    model_slug: initial.model_slug || "",
    model_name: initial.model_name || "",
    photographer: initial.photographer || "",
    stylist: initial.stylist || "",
    hair: initial.hair || "",
    makeup: initial.makeup || "",
  });
  const [models, setModels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/models").then((r) => setModels(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const pickModel = (id) => {
    const m = models.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      model_id: m ? m.id : "",
      model_slug: m ? m.slug : "",
      model_name: m ? m.name : f.model_name,
    }));
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      set("image_url", url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.message || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!form.brand.trim()) {
      toast.error("Brand name is required");
      return;
    }
    if (!form.image_url.trim()) {
      toast.error("Please upload or paste an image URL");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        brand: form.brand.trim(),
        subtitle: form.subtitle.trim() || null,
        caption: form.caption.trim() || null,
        image_url: form.image_url.trim(),
        external_link: form.external_link.trim() || null,
        order: Number(form.order) || 0,
        type: form.type || "campaign",
        model_id: form.model_id || null,
        model_slug: form.model_slug || null,
        model_name: form.model_name.trim() || null,
        photographer: form.photographer.trim() || null,
        stylist: form.stylist.trim() || null,
        hair: form.hair.trim() || null,
        makeup: form.makeup.trim() || null,
      };
      if (isNew) {
        await api.post("/admin/latest", payload);
        toast.success("Published");
      } else {
        await api.put(`/admin/latest/${initial.id}`, payload);
        toast.success("Updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="latest-editor"
      className="mt-8 border border-black/10 bg-[#FAFAF9] p-6 space-y-5"
    >
      <div className="flex items-center justify-between">
        <p className="label-overline text-zinc-500">{isNew ? "New work" : `Editing · ${initial.brand}`}</p>
        <button
          type="button"
          onClick={onCancel}
          className="p-1 text-zinc-500 hover:text-black"
          aria-label="Cancel"
        >
          <X size={16}/>
        </button>
      </div>

      {/* Image picker */}
      <div>
        <label className="label-overline text-zinc-700">Cover image *</label>
        <div className="mt-2 flex flex-col sm:flex-row gap-3">
          <div className="w-full sm:w-48 h-56 bg-white border border-black/10 overflow-hidden flex items-center justify-center relative">
            {form.image_url ? (
              <>
                <img src={form.image_url} alt="" className="w-full h-full object-cover"/>
                <button
                  type="button"
                  onClick={() => set("image_url", "")}
                  className="absolute top-1 right-1 bg-white/90 hover:bg-white p-1 border border-black/10"
                  aria-label="Remove image"
                >
                  <X size={12}/>
                </button>
              </>
            ) : (
              <ImageIcon size={22} className="text-zinc-300"/>
            )}
          </div>
          <div className="flex-1 flex flex-col gap-2">
            <label
              className={`btn-ghost inline-flex items-center gap-2 cursor-pointer justify-center sm:justify-start ${
                uploading ? "opacity-60 pointer-events-none" : ""
              }`}
            >
              {uploading ? <Loader2 size={14} className="animate-spin"/> : <UploadIcon size={14}/>}
              {uploading ? "Uploading…" : "Upload image"}
              <input
                type="file"
                accept="image/*,.heic,.heif"
                onChange={handleFile}
                data-testid="latest-image-input"
                className="hidden"
              />
            </label>
            <p className="text-xs text-zinc-500">or paste a public URL</p>
            <input
              type="url"
              value={form.image_url}
              onChange={(e) => set("image_url", e.target.value)}
              placeholder="https://…"
              data-testid="latest-image-url"
              className="bg-white border border-black/15 px-3 py-2 text-sm font-mono outline-none focus:border-black"
            />
          </div>
        </div>
      </div>

      <TextField
        label="Brand *"
        value={form.brand}
        onChange={(v) => set("brand", v)}
        placeholder="e.g. Carsicko"
        testid="latest-brand"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField
          label="Subtitle"
          value={form.subtitle}
          onChange={(v) => set("subtitle", v)}
          placeholder="e.g. Spring / Summer 2025"
          testid="latest-subtitle"
        />
        <TextField
          label="Order"
          value={String(form.order)}
          onChange={(v) => set("order", v)}
          placeholder="0 = first"
          type="number"
          testid="latest-order"
        />
      </div>

      {/* Type + featured talent */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label-overline text-zinc-700">Type</label>
          <select
            value={form.type}
            onChange={(e) => set("type", e.target.value)}
            data-testid="latest-type"
            className="mt-2 w-full bg-white border border-black/15 px-3 py-2 text-sm outline-none focus:border-black"
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label-overline text-zinc-700">Featured talent (roster)</label>
          <select
            value={form.model_id}
            onChange={(e) => pickModel(e.target.value)}
            data-testid="latest-model-select"
            className="mt-2 w-full bg-white border border-black/15 px-3 py-2 text-sm outline-none focus:border-black"
          >
            <option value="">— none / off-roster —</option>
            {models.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>
      </div>

      <TextField
        label="Talent name shown (free text — supports multiple names)"
        value={form.model_name}
        onChange={(v) => set("model_name", v)}
        placeholder="e.g. Mara Visser & Ella Kova"
        testid="latest-model-name"
      />

      {/* Production credits */}
      <div>
        <p className="label-overline text-zinc-700">Production credits</p>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Photographed by" value={form.photographer} onChange={(v) => set("photographer", v)} placeholder="Photographer" testid="latest-photographer"/>
          <TextField label="Styled by" value={form.stylist} onChange={(v) => set("stylist", v)} placeholder="Stylist" testid="latest-stylist"/>
          <TextField label="Hair by" value={form.hair} onChange={(v) => set("hair", v)} placeholder="Hair" testid="latest-hair"/>
          <TextField label="Makeup by" value={form.makeup} onChange={(v) => set("makeup", v)} placeholder="Makeup" testid="latest-makeup"/>
        </div>
      </div>

      <TextField
        label="Caption (shown under the tile)"
        value={form.caption}
        onChange={(v) => set("caption", v)}
        placeholder="e.g. Photographed by Dorian Lafargue"
        testid="latest-caption"
      />

      <TextField
        label="External link (optional)"
        value={form.external_link}
        onChange={(v) => set("external_link", v)}
        placeholder="https://…"
        type="url"
        testid="latest-external"
      />

      <div className="flex items-center gap-3 pt-3 border-t border-black/10">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="latest-save"
          className="btn-primary inline-flex items-center gap-2"
        >
          {saving ? <><Loader2 size={14} className="animate-spin"/> Saving…</> : <><Save size={14}/> {isNew ? "Publish" : "Save changes"}</>}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="btn-ghost"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, testid, type = "text" }) {
  return (
    <div>
      <label className="label-overline text-zinc-700">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-2 w-full bg-white border border-black/15 px-3 py-2 text-sm outline-none focus:border-black"
      />
    </div>
  );
}
